// ============================================================================
// CHANGO V5.0 — D2 Site Page renderer
// ============================================================================
// Drives a marketing page from the API contract published by
// /api/site/pages/:slug. Fetches once on mount, renders blocks in order.
//
// Block kinds (mirror lib/siteBlockSchema.js on the server):
//   hero | features | pricing | testimonials | cta | rich_text | media | faq
//
// §20 #19 reminder: pricing blocks carry sku_code only — never an inlined
// price. The actual price is fetched at click-time via /api/sku/:code or
// rendered as a "联系销售" placeholder when no price has been published.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { hostFor, urlFor } from '../lib/hosts.js';

const API_BASE = import.meta.env.VITE_API_BASE || '';

// Task #58 — CMS-authored CTAs may point at any intra-platform path
// (`/login`, `/register`, `/ops/*`, `/app/*`), but the site SPA only has
// marketing routes. We classify the href via `hostFor`:
//   • same-SPA (site) and not _blank → render with <Link> for SPA nav
//   • cross-SPA (app / ops / public / external) → plain <a> with the
//     absolute URL from `urlFor`; plain <a> is required because <Link>
//     would try to pushState an absolute href and at best stay on the
//     current host. A full document nav is the correct cross-host
//     behaviour — and in dev (single origin, env unset) `urlFor` returns
//     a relative path so the browser still does a normal in-SPA nav.
function CtaButton({ cta, variant = 'primary' }) {
  if (!cta) return null;
  const cls = variant === 'primary'
    ? 'inline-block rounded-md bg-chango-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-chango-700'
    : 'inline-block rounded-md border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50';

  const href = cta.href || '';
  const kind = hostFor(href);

  if (kind === 'site' && cta.target !== '_blank') {
    // Intra-site navigation — use router Link for SPA transition.
    return <Link to={href} className={cls}>{cta.label}</Link>;
  }

  // Cross-SPA or external. urlFor returns relative in dev (env unset) and
  // absolute in prod; for external (non-platform) hrefs it returns the href
  // unchanged, so `mailto:` / `tel:` / `https://` still pass through.
  const resolved = urlFor(href) || href;
  return (
    <a
      href={resolved}
      target={cta.target === '_blank' ? '_blank' : undefined}
      rel={cta.target === '_blank' ? 'noreferrer noopener' : undefined}
      className={cls}
    >{cta.label}</a>
  );
}

function HeroBlock({ payload }) {
  return (
    <section className="bg-gradient-to-br from-chango-50 to-white py-20">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
          {payload.title}
        </h1>
        {payload.subtitle && (
          <p className="mt-5 text-lg leading-8 text-slate-600">{payload.subtitle}</p>
        )}
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <CtaButton cta={payload.primary_cta} variant="primary" />
          <CtaButton cta={payload.secondary_cta} variant="ghost" />
        </div>
      </div>
    </section>
  );
}

function FeaturesBlock({ payload }) {
  return (
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-6">
        {payload.heading && (
          <h2 className="text-2xl font-semibold text-slate-900 text-center mb-10">
            {payload.heading}
          </h2>
        )}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {payload.items.map((it, i) => (
            <li key={i} className="card p-5">
              {it.icon && <div className="text-2xl mb-2" aria-hidden="true">{it.icon}</div>}
              <div className="font-semibold text-slate-800">{it.label}</div>
              {it.description && (
                <p className="mt-1 text-sm text-slate-600">{it.description}</p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PricingBlock({ payload }) {
  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {payload.plans.map((p) => (
            <li
              key={p.sku_code}
              className={[
                'card p-6 flex flex-col',
                p.featured ? 'border-2 border-chango-500 shadow-md' : ''
              ].join(' ')}
            >
              <div className="text-xs uppercase tracking-wide text-chango-700 font-semibold">
                {p.sku_code}
              </div>
              <h3 className="mt-1 text-xl font-semibold text-slate-900">{p.name}</h3>
              {p.tagline && <p className="mt-2 text-sm text-slate-600">{p.tagline}</p>}
              {/* Price is intentionally NOT pre-rendered — fetched at runtime */}
              <SkuPriceLine skuCode={p.sku_code} />
              {p.highlights?.length > 0 && (
                <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
                  {p.highlights.map((h, i) => (
                    <li key={i} className="flex gap-2">
                      <span aria-hidden="true">•</span><span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-6">
                <CtaButton cta={p.cta} variant={p.featured ? 'primary' : 'ghost'} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SkuPriceLine({ skuCode }) {
  const [state, setState] = useState({ status: 'loading', amount: null, currency: 'CNY' });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/sku/${encodeURIComponent(skuCode)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (cancelled) return;
        if (j && j.amount_rmb != null) {
          setState({ status: 'ok', amount: j.amount_rmb, currency: 'CNY' });
        } else {
          setState({ status: 'unpriced', amount: null, currency: 'CNY' });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unpriced', amount: null, currency: 'CNY' });
      });
    return () => { cancelled = true; };
  }, [skuCode]);

  if (state.status === 'loading') {
    return <div className="mt-3 h-7 w-24 rounded bg-slate-100 animate-pulse" />;
  }
  if (state.status === 'unpriced') {
    return (
      <div className="mt-3 text-sm text-slate-500">
        请联系销售获取价格
      </div>
    );
  }
  return (
    <div className="mt-3">
      <span className="text-3xl font-bold text-slate-900 tabular-nums">
        ¥{Number(state.amount).toLocaleString('zh-CN', {
          minimumFractionDigits: 2, maximumFractionDigits: 2
        })}
      </span>
      <span className="text-sm text-slate-500 ml-1">/ 月</span>
    </div>
  );
}

function TestimonialsBlock({ payload }) {
  return (
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-6">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {payload.items.map((t, i) => (
            <li key={i} className="card p-6">
              <blockquote className="text-slate-700 italic">“{t.quote}”</blockquote>
              <div className="mt-4 flex items-center gap-3">
                {t.avatar_url && (
                  <img src={t.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                )}
                <div>
                  <div className="font-semibold text-slate-800">{t.name}</div>
                  {t.title && <div className="text-xs text-slate-500">{t.title}</div>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function CtaBlock({ payload }) {
  return (
    <section className="py-16 bg-chango-700 text-white">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-2xl font-semibold">{payload.title}</h2>
        {payload.subtitle && (
          <p className="mt-3 text-chango-100">{payload.subtitle}</p>
        )}
        <div className="mt-6">
          <CtaButton cta={payload.primary_cta} variant="primary" />
        </div>
      </div>
    </section>
  );
}

function RichTextBlock({ payload }) {
  // Markdown rendering is deferred — for D2 we render as preformatted text.
  // (Adding `marked`/`remark` is a separate ticket once the lib choice is set.)
  return (
    <section className="py-12">
      <div className="max-w-3xl mx-auto px-6 prose prose-slate prose-sm whitespace-pre-wrap">
        {payload.body_md}
      </div>
    </section>
  );
}

function MediaBlock({ payload }) {
  return (
    <section className="py-12">
      <div className="max-w-4xl mx-auto px-6">
        {payload.media_type === 'image' && (
          <img src={payload.url} alt={payload.alt_text || ''} className="w-full rounded-lg" />
        )}
        {payload.media_type === 'video' && (
          <video controls src={payload.url} className="w-full rounded-lg" />
        )}
        {payload.media_type === 'embed' && (
          <iframe
            src={payload.url}
            title={payload.caption || 'embed'}
            className="w-full aspect-video rounded-lg"
            allow="autoplay; encrypted-media"
            sandbox="allow-scripts allow-same-origin allow-presentation"
          />
        )}
        {payload.caption && (
          <p className="mt-2 text-center text-xs text-slate-500">{payload.caption}</p>
        )}
      </div>
    </section>
  );
}

function FaqBlock({ payload }) {
  return (
    <section className="py-16">
      <div className="max-w-3xl mx-auto px-6 space-y-3">
        {payload.items.map((f, i) => (
          <details
            key={i}
            className="card px-5 py-4 group"
          >
            <summary className="cursor-pointer font-semibold text-slate-800 list-none flex justify-between items-center">
              <span>{f.question}</span>
              <span aria-hidden="true" className="text-slate-400 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">
              {f.answer_md}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

const BLOCK_RENDERERS = {
  hero:         HeroBlock,
  features:     FeaturesBlock,
  pricing:      PricingBlock,
  testimonials: TestimonialsBlock,
  cta:          CtaBlock,
  rich_text:    RichTextBlock,
  media:        MediaBlock,
  faq:          FaqBlock
};

function useSitePage(slug, locale = 'zh-CN') {
  const [state, setState] = useState({ status: 'loading', page: null, blocks: [], error: null });
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', page: null, blocks: [], error: null });
    fetch(`${API_BASE}/api/site/pages/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`)
      .then((r) => {
        if (r.status === 404) return { __notfound: true };
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (cancelled) return;
        if (j?.__notfound) {
          setState({ status: 'notfound', page: null, blocks: [], error: null });
        } else {
          setState({
            status: 'ready',
            page: j.page || null,
            blocks: Array.isArray(j.blocks) ? j.blocks : [],
            error: null
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error', page: null, blocks: [], error: err.message || '加载失败' });
      });
    return () => { cancelled = true; };
  }, [slug, locale]);
  return state;
}

export default function SitePageRenderer({ slug, locale }) {
  const { status, page, blocks, error } = useSitePage(slug, locale);

  // Update <title> and meta-description without a 3rd-party head manager —
  // small enough that we don't need react-helmet for D2.
  useEffect(() => {
    if (page?.title) document.title = page.title;
    if (page?.meta_description) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'description');
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', page.meta_description);
    }
  }, [page?.title, page?.meta_description]);

  if (status === 'loading') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-slate-400">
        加载中…
      </div>
    );
  }
  if (status === 'notfound') {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center text-slate-500">
        <p className="text-base">页面尚未发布</p>
        <p className="text-xs mt-2 text-slate-400">slug: {slug}</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-rose-600">
        加载失败：{error}
      </div>
    );
  }

  return (
    <>
      {blocks.map((b) => {
        const Renderer = BLOCK_RENDERERS[b.kind];
        if (!Renderer) return null;        // unknown kind → silently skip
        return <Renderer key={b.id} payload={b.payload} />;
      })}
    </>
  );
}
